import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../SEO/SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const stagger = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const services = [
  {
    id: 'fractional-cto',
    title: 'Fractional CTO & Technical Leadership',
    tags: ['Fractional CTO', 'CTO as a Service', 'Interim CTO', 'Engineering Leadership'],
    paragraphs: [
      'Most startups and mid-market companies hit a ceiling where they need executive-level technical leadership but cannot justify—or afford—a full-time CTO. That is exactly the gap I fill. As a fractional CTO, I embed directly into your organization, owning your technology strategy, engineering roadmap, and technical hiring decisions on a part-time or project basis. This is not advisory from the sidelines. I sit in your leadership meetings, run your architecture reviews, and hold your engineering team accountable to delivery milestones.',
      'My fractional CTO services cover the full scope of what a traditional Chief Technology Officer delivers: technology strategy aligned to business objectives, build-versus-buy analysis, vendor evaluation, technical due diligence for fundraising or M&A, and engineering team structure and process design. I have led teams ranging from three-person startup squads to fifty-plus engineer organizations across SaaS, fintech, healthtech, and enterprise software.',
      'The startup CTO role is uniquely demanding because it requires someone who can operate at the board level and still get into the code when needed. I bring that range. Whether you are a Series A company building your first engineering team, a Series B company scaling your platform, or a bootstrapped business that needs to modernize its tech stack, interim CTO and fractional CTO engagements give you senior technical leadership without the full-time overhead.',
    ],
    deliverables: [
      'Technology strategy and multi-year engineering roadmap',
      'Engineering team assessment, hiring plans, and org design',
      'Technical due diligence documentation for investors or acquirers',
      'Architecture review and modernization planning',
      'Build-vs-buy analysis and vendor evaluation frameworks',
      'Board-ready technology presentations and KPI dashboards',
    ],
  },
  {
    id: 'ai-implementation',
    title: 'AI & Agentic Business Implementation',
    tags: ['OpenAI', 'Anthropic', 'LangChain', 'RAG Pipelines', 'AI Agents'],
    paragraphs: [
      'The gap between AI hype and AI value is execution. Every company wants to integrate large language models and intelligent automation, but very few have the engineering depth to do it well. I specialize in enterprise AI implementation—taking your business processes and building real, production-grade AI systems that reduce costs, accelerate workflows, and create competitive advantage. This is not proof-of-concept work. I build AI systems that run in production, at scale, with proper guardrails.',
      'My AI consulting covers the full implementation lifecycle. I start with AI strategy consulting—identifying the highest-ROI use cases in your business, selecting the right foundation models (GPT-4, Claude, Gemini, open-source LLMs), and designing the architecture. From there, I build. That includes RAG pipeline development for knowledge-intensive applications, agentic workflow systems that autonomously execute multi-step business processes, LLM integration into existing SaaS platforms, fine-tuning and prompt engineering, and AI automation for operations like customer support, document processing, and data extraction.',
      'What sets my approach apart is that I treat AI implementation as an engineering discipline, not a science experiment. Every system I build includes proper evaluation frameworks, cost monitoring, latency optimization, fallback handling, and human-in-the-loop safeguards where appropriate. If you are evaluating AI strategy consulting firms, the question to ask is whether they have actually shipped AI features to production users. I have—repeatedly, across multiple industries.',
    ],
    deliverables: [
      'AI opportunity assessment and ROI-prioritized implementation roadmap',
      'Production RAG pipelines with vector databases (Pinecone, Weaviate, pgvector)',
      'Agentic workflow systems using LangChain, LangGraph, and CrewAI',
      'LLM integration into existing products and internal tools',
      'Evaluation frameworks, cost dashboards, and performance monitoring',
      'Custom fine-tuning pipelines and prompt engineering libraries',
    ],
  },
  {
    id: 'cloud-native',
    title: 'Cloud Native Development & Architecture',
    tags: ['Kubernetes', 'Docker', 'Serverless', 'Microservices', 'Event-Driven Architecture'],
    paragraphs: [
      'Cloud native development is not just about running containers—it is a fundamental shift in how software is designed, built, deployed, and operated. I architect cloud native applications from the ground up, using microservices architecture, containerization, and serverless computing patterns that give your platform the scalability, resilience, and deployment velocity that modern markets demand. Whether you are building a new product or decomposing a monolith, I bring deep hands-on experience across every layer of the cloud native stack.',
      'My cloud architecture consulting starts with understanding your domain and your scale targets, then designing the right set of patterns. Not everything needs to be microservices, and not every workload should be serverless. I make pragmatic decisions based on your team size, operational maturity, and business requirements. That said, when microservices architecture is the right call, I design service boundaries using domain-driven design principles, implement event-driven communication with Kafka or Pub/Sub, and build out Kubernetes consulting engagements that include cluster architecture, Helm chart development, service mesh configuration with Istio or Linkerd, and GitOps deployment workflows.',
      'On the serverless side, I design serverless architecture on AWS Lambda, Google Cloud Functions, and Azure Functions for event-driven workloads, API backends, and data processing pipelines. The goal is always the same: minimize operational overhead while maximizing scalability and developer productivity. I have helped teams go from monthly deploys to multiple production releases per day through cloud native architecture patterns combined with robust CI/CD automation.',
    ],
    deliverables: [
      'Cloud native architecture design and technical specifications',
      'Kubernetes cluster design, deployment pipelines, and Helm chart libraries',
      'Microservices decomposition strategy and domain-driven service boundaries',
      'Serverless application architecture on AWS Lambda, Cloud Functions, or Azure Functions',
      'Event-driven architecture with Kafka, Pub/Sub, or EventBridge',
      'Container orchestration strategy and migration from monolithic systems',
    ],
  },
  {
    id: 'multi-cloud',
    title: 'Multi-Cloud Strategy & Migration',
    tags: ['AWS', 'GCP', 'Azure', 'Terraform', 'Cloud Migration'],
    paragraphs: [
      'Cloud migration is one of the most consequential decisions a company makes, and multi-cloud strategy adds another layer of complexity. I provide cloud migration consulting that goes far beyond lift-and-shift. I evaluate your existing infrastructure, map your workloads to the right cloud providers, and design migration plans that minimize risk and downtime. As someone who works deeply across AWS, GCP, and Azure, I bring an unbiased perspective on where each platform excels and where it falls short for your specific requirements.',
      'My AWS consulting, GCP consulting, and Azure consulting engagements are grounded in real-world operational experience. I have designed and operated production systems on all three major clouds, and I understand the nuanced differences in networking, IAM, managed services, and pricing models. Multi-cloud strategy is not just about avoiding vendor lock-in—it is about placing workloads where they perform best and cost the least. That might mean running your AI/ML workloads on GCP for Vertex AI and TPU access, your core application on AWS for the breadth of managed services, and your enterprise integrations on Azure for Active Directory and Microsoft 365 compatibility.',
      'Cloud cost optimization is a critical part of every engagement. I have helped organizations reduce their monthly cloud spend by 30-50% through right-sizing, reserved capacity planning, spot instance strategies, architectural refactoring, and FinOps process implementation. Whether you are planning a greenfield cloud deployment, migrating from on-premises, or optimizing an existing multi-cloud environment, I deliver a strategy that balances performance, cost, and operational simplicity.',
    ],
    deliverables: [
      'Cloud readiness assessment and workload-to-provider mapping',
      'Multi-cloud architecture design with Terraform infrastructure as code',
      'Cloud migration runbooks with phased rollout and rollback plans',
      'Cloud cost optimization audits and FinOps implementation',
      'Provider-specific architecture reviews (AWS Well-Architected, GCP, Azure)',
      'Vendor negotiation support and reserved capacity planning',
    ],
  },
  {
    id: 'data-analytics',
    title: 'Data & Analytics',
    tags: ['Snowflake', 'BigQuery', 'dbt', 'Looker', 'Modern Data Stack'],
    paragraphs: [
      'Data is only an asset if you can actually use it. I build data infrastructure that turns raw operational data into business intelligence your team acts on daily. My data analytics consulting covers the full modern data stack—from ingestion and transformation to warehousing, visualization, and self-service analytics. I work with Snowflake, BigQuery, Databricks, and Redshift, and I pick the right platform based on your data volumes, query patterns, team capabilities, and budget.',
      'Data pipeline architecture is where most analytics projects succeed or fail. I design and implement robust ELT pipelines using tools like Fivetran, Airbyte, and custom connectors for data ingestion, dbt for transformation and modeling, and orchestration platforms like Airflow or Dagster to keep everything running reliably. The result is a data platform where every metric has a clear definition, data quality is monitored and tested, and your analysts and business users can self-serve without depending on engineering for every question.',
      'My business intelligence consulting extends beyond building dashboards. I work with stakeholders to define the metrics that actually matter, design dimensional models that support flexible analysis, and implement BI tools like Looker, Metabase, or Power BI with proper semantic layers. For companies dealing with real-time analytics requirements, I design streaming pipelines using Kafka, Pub/Sub, and Flink that deliver sub-second data freshness. The goal is always to make data accessible, trustworthy, and actionable across the entire organization.',
    ],
    deliverables: [
      'Modern data stack architecture design and platform selection',
      'Data pipeline development with dbt, Airflow, Fivetran, and Airbyte',
      'Snowflake consulting: warehouse design, optimization, and cost management',
      'BigQuery consulting: dataset architecture, partitioning, and query optimization',
      'BI dashboard development with Looker, Metabase, or Power BI',
      'Data quality frameworks, monitoring, and governance implementation',
    ],
  },
  {
    id: 'security-devops',
    title: 'Security, DevOps & Operational Excellence',
    tags: ['GitHub Actions', 'SOC 2', 'Terraform', 'Observability', 'Zero Trust'],
    paragraphs: [
      'Shipping fast means nothing if your systems are insecure, unreliable, or impossible to debug in production. I build DevOps practices and security postures that let engineering teams move quickly with confidence. My DevOps consulting starts with your deployment pipeline: I design and implement CI/CD pipelines using GitHub Actions, GitLab CI, CircleCI, or Jenkins that automate testing, security scanning, infrastructure provisioning, and production deployments. The target is always the same—get to a place where deploying to production is boring, because it is fully automated, tested, and reversible.',
      'Infrastructure as code is non-negotiable in any environment I architect. I use Terraform for multi-cloud infrastructure management, Pulumi for teams that prefer general-purpose languages, and CloudFormation or CDK for AWS-native shops. Every piece of infrastructure is version-controlled, peer-reviewed, and deployed through automated pipelines. Combined with GitOps workflows, this gives your team complete auditability and the ability to reproduce any environment from code.',
      'On the security side, I help companies achieve and maintain SOC 2 compliance, implement cloud security consulting best practices, and build zero-trust network architectures. I set up observability platforms using Datadog, Grafana, or New Relic that give your team real-time visibility into application performance, error rates, and infrastructure health. Alerting is designed to be actionable—not noisy. I have helped teams reduce their mean time to detection from hours to minutes and their mean time to recovery from days to under an hour through proper incident response processes and runbook automation.',
    ],
    deliverables: [
      'CI/CD pipeline design and implementation (GitHub Actions, GitLab CI)',
      'Infrastructure as code with Terraform, Pulumi, or CDK',
      'SOC 2 compliance readiness assessment and control implementation',
      'Observability platform setup with Datadog, Grafana, or New Relic',
      'Cloud security audit and zero-trust architecture design',
      'Incident response playbooks and on-call process design',
    ],
  },
];

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Fractional CTO Services & Technology Consulting"
        description="North provides fractional CTO services, AI strategy implementation, cloud architecture consulting, multi-cloud strategy, data analytics, and engineering leadership for startups and mid-market companies."
        path="/services"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.northinteractive.co/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Services',
                item: 'https://www.northinteractive.co/services',
              },
            ],
          })}
        </script>
      </Helmet>

      {/* ──────────────────────────── HERO ──────────────────────────── */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-cream pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-8 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Services
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-dark-slate mb-6 tracking-tight leading-tight"
            >
              Fractional CTO Services
              <br />
              <span className="text-muted-blue-grey">& Technology Consulting</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl md:text-2xl text-muted-grey max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Executive-level engineering leadership, AI implementation, cloud architecture,
              and data analytics — delivered fractionally for startups and mid-market companies
              that need senior technical leadership without the full-time overhead.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-dark-slate text-white rounded-full font-semibold text-lg hover:bg-deep-charcoal transition-all shadow-lg hover:shadow-xl inline-block"
              >
                Book a Strategy Call
              </motion.a>
              <Link
                to="/"
                className="px-8 py-4 bg-white text-dark-slate rounded-full font-semibold text-lg border-2 border-gray-200 hover:border-gray-300 transition-all inline-block"
              >
                Back to Home
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ──────────────────── SERVICE SECTIONS ──────────────────── */}
      {services.map((service, index) => {
        const isEven = index % 2 === 0;

        return (
          <section
            key={service.id}
            id={service.id}
            className={`py-24 px-6 ${isEven ? 'bg-white' : 'bg-cream'}`}
          >
            <div className="max-w-5xl mx-auto">
              {/* Tags */}
              <motion.div
                {...stagger}
                transition={{ duration: 0.5 }}
                className="flex flex-wrap gap-2 mb-6"
              >
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 text-muted-grey text-sm font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>

              {/* H2 Title */}
              <motion.h2
                {...fadeIn}
                className="text-4xl md:text-5xl font-bold text-dark-slate mb-8 tracking-tight leading-tight"
              >
                {service.title}
              </motion.h2>

              {/* Paragraphs */}
              <div className="space-y-6 mb-12">
                {service.paragraphs.map((para, pIdx) => (
                  <motion.p
                    key={pIdx}
                    {...stagger}
                    transition={{ duration: 0.5, delay: 0.1 * (pIdx + 1) }}
                    className="text-lg text-muted-grey leading-relaxed"
                  >
                    {para}
                  </motion.p>
                ))}
              </div>

              {/* What We Deliver */}
              <motion.div
                {...fadeIn}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gradient-to-br from-dark-navy to-deep-charcoal rounded-3xl p-10 md:p-12 text-white relative overflow-hidden"
              >
                {/* Decorative blurs */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-warm-tan opacity-15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-muted-blue-grey opacity-15 rounded-full blur-3xl" />

                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-6">What We Deliver</h3>
                  <ul className="grid md:grid-cols-2 gap-4">
                    {service.deliverables.map((item, dIdx) => (
                      <motion.li
                        key={dIdx}
                        {...stagger}
                        transition={{ duration: 0.4, delay: 0.05 * dIdx }}
                        className="flex items-start gap-3"
                      >
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-warm-tan shrink-0" />
                        <span className="text-gray-300 leading-relaxed">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* ──────────────────── HOW WE WORK ──────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Engagement Model
              </span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-6 tracking-tight">
              How We Work
            </h2>

            <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
              Fractional does not mean part-time attention. It means you get the
              same caliber of technical leadership that Fortune 500 companies have,
              structured to fit your stage and budget.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery & Assessment',
                description:
                  'We start with a deep dive into your technology landscape, business objectives, team structure, and current challenges. I assess where you are today and identify the highest-leverage areas for improvement. This typically takes one to two weeks and produces a prioritized technology roadmap.',
              },
              {
                step: '02',
                title: 'Embedded Execution',
                description:
                  'I integrate into your leadership team on a fractional basis — typically two to three days per week. I attend standups, lead architecture decisions, conduct code reviews, interview engineering candidates, and drive execution on the roadmap. You get a working CTO, not a consultant who writes a report and disappears.',
              },
              {
                step: '03',
                title: 'Transition & Scale',
                description:
                  'As your organization matures, I help you build the internal leadership capability to sustain momentum. That might mean hiring your full-time CTO or VP of Engineering, establishing engineering management practices, and ensuring knowledge transfer. The goal is always to build you an organization that does not depend on me long-term.',
              },
            ].map((phase, idx) => (
              <motion.div
                key={phase.step}
                {...stagger}
                transition={{ duration: 0.5, delay: 0.15 * idx }}
                className="bg-cream rounded-3xl p-8 border border-gray-100"
              >
                <div className="text-5xl font-bold text-gray-200 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold text-dark-slate mb-3">{phase.title}</h3>
                <p className="text-muted-grey leading-relaxed">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────── INDUSTRIES & CONTEXT ────────────────── */}
      <section className="py-24 px-6 bg-cream">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeIn}>
            <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-8 tracking-tight">
              Who This Is For
            </h2>

            <div className="space-y-6 text-lg text-muted-grey leading-relaxed mb-12">
              <p>
                I work primarily with startups (Seed through Series C) and mid-market companies
                in the $5M to $200M revenue range that are technology-enabled but do not have a
                dedicated senior technical executive. My clients span SaaS, fintech, healthtech,
                e-commerce, logistics, and professional services — the common thread is that
                technology is a growth lever for the business, not just a cost center.
              </p>
              <p>
                Typical engagements start because of a specific trigger: preparing for a fundraise
                that requires technical due diligence, needing to scale an engineering team quickly,
                modernizing legacy infrastructure, launching an AI initiative, or recovering from a
                failed project that needs experienced leadership to get back on track. If any of
                those sound familiar, we should talk.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {[
                'SaaS & Software',
                'Fintech & Payments',
                'Healthtech',
                'E-Commerce',
                'Logistics & Supply Chain',
                'Professional Services',
                'Media & Publishing',
                'Real Estate Tech',
              ].map((industry) => (
                <span
                  key={industry}
                  className="px-4 py-2 bg-white text-dark-slate text-sm font-medium rounded-full border border-gray-100"
                >
                  {industry}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ──────────────────────── CTA ──────────────────────── */}
      <section className="py-24 px-6 bg-gradient-to-br from-dark-navy to-deep-charcoal relative overflow-hidden">
        {/* Decorative blurs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-warm-tan opacity-10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-muted-blue-grey opacity-10 rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Talk Technology Strategy?
            </h2>

            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Book a 30-minute strategy call. No pitch decks, no sales pressure — just a
              direct conversation about your technology challenges and whether fractional
              CTO services are the right fit for where you are today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-dark-navy rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl inline-block"
              >
                Book a Strategy Call
              </motion.a>
              <a
                href="mailto:info@northinteractive.co"
                className="px-8 py-4 text-white border-2 border-white/30 rounded-full font-semibold text-lg hover:border-white/60 transition-all inline-block"
              >
                info@northinteractive.co
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
